/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState, useEffect, useRef } from 'react';
import { pdfjs } from 'react-pdf';
import { toast } from 'react-toastify';
import { IcertificateDetails } from '../../TypesAndInterfaces';
import { getCertificatePdfUrl } from '../utils/certificateUtils';
import {
  sendChooseCertificatePopupShownEvent,
  sendChooseCertificateConfirmClickedEvent,
  sendCertificateDownloadEvent,
} from '../utils/certificateGAEvents';
import CertificateSelectionModal from './CertificateSelectionModal';

type DownloadType = 'pdf' | 'image';

interface DownloadModalProps {
  isOpen: boolean;
  onClose: () => void;
  allCertificates: IcertificateDetails[];
  selectedIndex: number;
  isCareerPath: boolean;
  downloadType: DownloadType;
}

const DownloadModal: React.FC<DownloadModalProps> = ({
  isOpen,
  onClose,
  allCertificates,
  selectedIndex,
  isCareerPath,
  downloadType,
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const popupShownForCurrentOpen = useRef(false);

  const partnerCertCount = allCertificates.filter((c) => c.isPartnerCertificate).length;
  const currentCert = allCertificates[selectedIndex];

  // Reset flag when modal closes
  useEffect(() => {
    if (!isOpen) {
      popupShownForCurrentOpen.current = false;
    }
  }, [isOpen]);

  // Track popup shown event (only once per modal open)
  useEffect(() => {
    if (isOpen && allCertificates.length > 0 && !popupShownForCurrentOpen.current) {
      popupShownForCurrentOpen.current = true;
      sendChooseCertificatePopupShownEvent({
        popup_type: 'download',
        certificates_count: allCertificates.length,
        certificate_ids: allCertificates.map((c) => c.certificate?.id).filter(Boolean),
        course_name: currentCert?.courseName,
        course_id: currentCert?.certificate?._id,
        partner_certs_available_count: partnerCertCount,
      });
    }
  }, [isOpen, allCertificates, currentCert, partnerCertCount]);

  const handleDownloadPdfs = async (certs: IcertificateDetails[]): Promise<string[]> => {
    const successfulIds: string[] = [];
    for (let idx = 0; idx < certs.length; idx += 1) {
      const cert = certs[idx];
      const pdfUrl = getCertificatePdfUrl(cert.certificate.id, isCareerPath);
      try {
        // eslint-disable-next-line no-await-in-loop
        const response = await fetch(pdfUrl);
        // eslint-disable-next-line no-await-in-loop
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `${cert.certificate.id}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
        successfulIds.push(cert.certificate.id);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(`Failed to download PDF for certificate ${cert.certificate.id}:`, err);
      }
    }
    return successfulIds;
  };

  const handleDownloadImages = async (certs: IcertificateDetails[]): Promise<string[]> => {
    const successfulIds: string[] = [];
    for (let idx = 0; idx < certs.length; idx += 1) {
      const cert = certs[idx];
      const pdfUrl = getCertificatePdfUrl(cert.certificate.id, isCareerPath);
      try {
        // eslint-disable-next-line no-await-in-loop
        const loadingTask = pdfjs.getDocument(pdfUrl);
        // eslint-disable-next-line no-await-in-loop
        const pdf = await loadingTask.promise;
        // eslint-disable-next-line no-await-in-loop
        const page = await pdf.getPage(1);
        const scale = 1;
        const viewport = page.getViewport({ scale });
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.height = viewport.height;
        canvas.width = viewport.width;
        const renderContext: any = {
          canvasContext: context,
          viewport,
        };
        // eslint-disable-next-line no-await-in-loop
        await page.render(renderContext).promise;
        const downloadImage = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.href = downloadImage;
        link.download = `${cert.certificate.id}.png`;
        link.click();
        successfulIds.push(cert.certificate.id);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(`Failed to download image for certificate ${cert.certificate.id}:`, err);
      }
    }
    return successfulIds;
  };

  const handleSubmit = async (selectedCertificates: IcertificateDetails[]) => {
    if (selectedCertificates.length === 0) {
      return;
    }

    const selectedCertIds = selectedCertificates.map((c) => c.certificate?.id).filter(Boolean);
    const actionType = downloadType === 'pdf' ? 'download_pdf' : 'download_image';

    // Track confirm clicked event
    sendChooseCertificateConfirmClickedEvent({
      action_type: actionType,
      certificates_count: selectedCertificates.length,
      certificate_ids: selectedCertIds,
      course_name: currentCert?.courseName,
      course_id: currentCert?.certificate?._id,
      partner_certs_available_count: partnerCertCount,
    });

    setIsDownloading(true);
    try {
      let successfulIds: string[] = [];
      if (downloadType === 'pdf') {
        successfulIds = await handleDownloadPdfs(selectedCertificates);
      } else {
        successfulIds = await handleDownloadImages(selectedCertificates);
      }

      // Track successful download (only for actually downloaded certificates)
      if (successfulIds.length > 0) {
        sendCertificateDownloadEvent({
          download_type: downloadType,
          certificates_count: successfulIds.length,
          certificate_ids: successfulIds,
          course_name: currentCert?.courseName,
          course_id: currentCert?.certificate?._id,
          partner_certs_available_count: partnerCertCount,
        });
      }

      // Show toast if some downloads failed
      const failedCount = selectedCertificates.length - successfulIds.length;
      if (failedCount > 0 && successfulIds.length > 0) {
        toast.warning(`${failedCount} certificate(s) failed to download.`);
      } else if (failedCount > 0 && successfulIds.length === 0) {
        toast.error('Failed to download certificates. Please try again.');
      }
    } finally {
      setIsDownloading(false);
      onClose();
    }
  };

  const titleText = downloadType === 'pdf' ? 'Download as PDF' : 'Download as Image';
  const descriptionText =
    downloadType === 'pdf'
      ? 'Select the certificates you would like to download as PDF files.'
      : 'Select the certificates you would like to download as image files.';

  return (
    <CertificateSelectionModal
      isOpen={isOpen}
      onClose={onClose}
      allCertificates={allCertificates}
      selectedIndex={selectedIndex}
      title={titleText}
      description={descriptionText}
      submitButtonText="Download"
      onSubmit={handleSubmit}
      isLoading={isDownloading}
      showSelectAll
    />
  );
};

export default DownloadModal;
